/**
 * This file contains utility types and functions for working with our concrete
 * providers.
 *
 * Important note about these types: There is no way to constrain a type with a
 * generic bound like <T extends ProviderType> to be only a single value from
 * that set. That means that technically "tag" | "territory" is a valid value
 * for this generic, and ProviderId<"tag" | "territory"> will be the union of
 * TagId and TerritoryId. This is convenient (you can write mostly correct
 * generic code without type issues), but not always perfect.
 *
 * For example, provider = getProvider<ProviderType>("tag") will actually return
 * tagProvider, but will be typed as a RemoteLookupProvider where each
 * constituent generic is the union of all possible types. Typescript would
 * happily accept calling this provider.mapItemToId(territoryItem), even though
 * that is incorrect. The main thing I want to emphasize is that you need to be
 * kind of careful when writing generic code.
 */

import { providers } from "./providers";
import type { RemoteLookupProvider } from "./types";

export type Providers = typeof providers;

export type ProviderType = keyof Providers;

export type ProviderId<T extends ProviderType> =
  Providers[T] extends RemoteLookupProvider<infer Id, any, any> ? Id : never;

export type ProviderItem<T extends ProviderType> =
  Providers[T] extends RemoteLookupProvider<any, infer Item, any> ? Item : never;

export type ProviderFilters<T extends ProviderType> =
  Providers[T] extends RemoteLookupProvider<any, any, infer Filters> ? Filters : never;

export type Provider<T extends ProviderType> = RemoteLookupProvider<
  ProviderId<T>,
  ProviderItem<T>,
  ProviderFilters<T>
>;

/**
 * Returns whether the given type is a valid provider type.
 */
export function isProviderType(type: string): type is ProviderType {
  return Object.prototype.hasOwnProperty.call(providers, type);
}

/**
 * Returns the provider for the given type, or raises an error if it doesn't exist.
 */
export function getProvider<T extends ProviderType>(type: T): Provider<T> {
  if (!isProviderType(type)) {
    throw new Error(`provider of type ${String(type)} does not exist`);
  }
  return providers[type] as unknown as Provider<T>;
}

/**
 * Transforms providers to a new mapped type with a transform function. The idea
 * is that you use this to go from the set of providers {[type]: Provider} to,
 * say, a set of fetchers {[type]: Fetcher<T>}, while keeping the mapped types
 * correct.
 */
export function mapProviders<Result extends { [T in ProviderType]: any }>(
  // The return type should be Result[T], but that causes a compiler error
  // because Result[T] is widened to the union of all possible return types. So
  // we have to use unknown here and hope the Result type is defined correctly.
  // Given the caller is using ReturnType<typeof myTransform<T>>, it should be.
  transformFn: <T extends ProviderType>(type: T, provider: Provider<T>) => unknown
): Result {
  return Object.entries(providers).reduce((acc, ent) => {
    const [type, provider] = ent as unknown as [ProviderType, Provider<ProviderType>];
    acc[type] = transformFn(type, provider);
    return acc;
  }, {} as Result);
}
